import React, { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import Loader from "../components/Loader";
import OverviewSection from "./Observability/sections/OverviewSection";
import MembershipDynamicsSection from "./Observability/sections/MembershipDynamicsSection";
import FinancialImpactSection from "./Observability/sections/FinancialImpactSection";
import MonthRangeSelector from "./Observability/components/MonthRangeSelector";
import { useTargetGroupAnalytics } from "./Observability/hooks/useTargetGroupAnalytics";
import { useGroups } from "./hooks/useGroups";
import { SearchHeader } from "../QrCodeManagement/components/SearchHeader";
import type { AnalyticsScope, PeriodRange, MonthKey } from "../../types/targetGroupAnalytics";
import type { Group } from "../../types/group";
import "./Observability/TargetGroupObservability.css";

const GLOBAL_SCOPE: AnalyticsScope = { kind: "global" };
const OBS_BASE = "/target-groups/observability";

const trailing12Months = (): PeriodRange => {
  const now = new Date();
  const to: MonthKey = { year: now.getFullYear(), month: now.getMonth() + 1 };
  const fromDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const from: MonthKey = { year: fromDate.getFullYear(), month: fromDate.getMonth() + 1 };
  return { from, to };
};

const ArrayCell = ({ items }: { items: string[] | undefined | null }) => (
  <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
    {(items ?? []).join(", ")}
  </span>
);

const pathToTab = (pathname: string): number => {
  if (pathname.includes("/memberships")) return 2;
  if (pathname.includes("/financial")) return 3;
  if (pathname.includes("/list")) return 1;
  return 0; // /target-groups or /target-groups/observability → Overview
};

const TargetGroupsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const activeTab = pathToTab(location.pathname);

  // List state
  const { groups, initialLoading: groupsLoading, refreshing } = useGroups(toast);
  const [search, setSearch] = useState("");

  // Analytics state
  const [period, setPeriod] = useState<PeriodRange>(trailing12Months);
  const [dynamicsEnabled, setDynamicsEnabled] = useState(() => activeTab >= 2);
  const [financialEnabled, setFinancialEnabled] = useState(() => activeTab >= 3);

  const { overview, dynamics, financial, loading: analyticsLoading, dynamicsLoading, error, refetch } =
    useTargetGroupAnalytics(GLOBAL_SCOPE, period, { dynamicsEnabled, financialEnabled });

  const handleTabChange = useCallback((e: { index: number }) => {
    if (e.index === 0) navigate("/target-groups");
    if (e.index === 1) navigate("/target-groups/list");
    if (e.index === 2) { navigate(`${OBS_BASE}/memberships`); setDynamicsEnabled(true); }
    if (e.index === 3) { navigate(`${OBS_BASE}/financial`); setFinancialEnabled(true); }
  }, [navigate]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const term = search.trim().toLowerCase();
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(term) ||
        g.members?.some((m) => m.email.toLowerCase().includes(term)) ||
        g.stationIds?.some((s) => s.toLowerCase().includes(term)),
    );
  }, [groups, search]);

  return (
    <div className="layout-main">
      <div className="surface-section">
        <div className="grid">
          <div className="col-12">
      <div className="target-group-observability p-4">
        <Toast
          ref={toast}
          position="top-right"
          appendTo={typeof window !== "undefined" ? document.body : undefined}
        />
        <div className="tg-page-header flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
          <h2 className="m-0">Target Groups</h2>
          {activeTab !== 1 && (
            <MonthRangeSelector value={period} onChange={setPeriod} />
          )}
        </div>

        <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
        <TabPanel header="Overview">
          {analyticsLoading && <Loader style={{ minHeight: "50vh" }} />}
          {!analyticsLoading && error && (
            <div
              className="flex flex-column align-items-center gap-3"
              style={{ minHeight: "40vh", justifyContent: "center" }}
            >
              <Message severity="error" text={error} />
              <Button label="Retry" icon="pi pi-refresh" onClick={refetch} />
            </div>
          )}
          {!analyticsLoading && !error && overview && (
            <OverviewSection data={overview} />
          )}
        </TabPanel>

        <TabPanel header="List">
          <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-3 mb-3">
            <div className="w-full md:w-20rem">
              <SearchHeader
                value={search}
                onChange={setSearch}
                placeholder="Search groups..."
                helperText="Search by user email or station ID"
              />
            </div>
            <Button
              label="New Group"
              icon="pi pi-plus"
              onClick={() => navigate("/target-groups/new")}
            />
          </div>
          {groupsLoading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={filteredGroups}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 25, 50]}
              dataKey="groupId"
              emptyMessage="No groups found."
              loading={refreshing}
              stripedRows
              showGridlines={false}
              tableStyle={{ minWidth: "50rem" }}
              onRowClick={(e: DataTableRowClickEvent) =>
                navigate(`/target-groups/${(e.data as Group).groupId}`)
              }
              rowClassName={() => "cursor-pointer"}
            >
              <Column field="name" header="Name" sortable style={{ width: "20%" }} />
              <Column field="ownerEmail" header="Owner" sortable style={{ width: "20%" }} />
              <Column
                field="members"
                header="Users"
                style={{ width: "40%" }}
                body={(row: Group) => <ArrayCell items={row.members?.map((m) => m.email)} />}
              />
              <Column
                field="stationIds"
                header="Stations"
                style={{ width: "40%" }}
                body={(row: Group) => <ArrayCell items={row.stationIds} />}
              />
            </DataTable>
          )}
        </TabPanel>

        <TabPanel header="Membership dynamics">
          {dynamicsLoading ? (
            <Loader style={{ minHeight: "40vh" }} />
          ) : dynamics ? (
            <MembershipDynamicsSection data={dynamics} />
          ) : dynamicsEnabled ? (
            <div
              className="flex flex-column align-items-center gap-3"
              style={{ minHeight: "40vh", justifyContent: "center" }}
            >
              <Message severity="warn" text="Membership dynamics could not be loaded." />
              <Button label="Retry" icon="pi pi-refresh" onClick={refetch} />
            </div>
          ) : null}
        </TabPanel>

        <TabPanel header="Financial impact">
          <FinancialImpactSection financial={financial} />
        </TabPanel>
        </TabView>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetGroupsPage;
